import { useEffect, useState } from 'react';
import { Footer } from './Footer';
import { PageBackground } from './PageBackground';
import { SEO, pageSEO } from './SEO';

interface User {
  username: string;
  displayName: string;
  avatarUrl: string;
  subscriptionStatus: 'active' | 'inactive' | 'past_due' | 'canceled';
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readSubscriptionStatus(value: unknown): User['subscriptionStatus'] {
  return value === 'active' || value === 'past_due' || value === 'canceled' ? value : 'inactive';
}

function getSubscriptionBadgeLabel(status: User['subscriptionStatus']): string {
  return status === 'active' ? 'Pro' : status === 'past_due' ? 'Past Due' : 'Free';
}

export function LandingPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const serverUrl = import.meta.env.DEV
    ? `${window.location.protocol}//localhost:3001`
    : `${window.location.protocol}//${window.location.host}`;

  useEffect(() => {
    fetch(`${serverUrl}/auth/me`, { credentials: 'include' })
      .then(res => res.ok ? (res.json() as Promise<Record<string, unknown>>) : null)
      .then(data => {
        if (data && !data.error) {
          const twitchUsername = readString(data.twitchUsername ?? data.username);
          const twitchDisplayName = readString(data.twitchDisplayName ?? data.displayName) || twitchUsername || 'Streamer';
          const rawAvatarUrl = readString(data.avatarUrl ?? data.profileImageUrl);
          const avatarUrl = rawAvatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(twitchUsername || 'User')}&background=9333ea&color=fff`;
          setUser({
            username: twitchUsername,
            displayName: twitchDisplayName,
            avatarUrl,
            subscriptionStatus: readSubscriptionStatus(data.subscriptionStatus),
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [serverUrl]);

  const handleLogin = () => {
    window.location.href = `${serverUrl}/auth/twitch`;
  };

  const handleLogout = () => {
    void fetch(`${serverUrl}/auth/logout`, { method: 'POST', credentials: 'include' })
      .then(() => {
        setUser(null);
      });
  };

  // Don't block render on auth - show landing page immediately
  // Auth state will hydrate when ready

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col relative overflow-hidden">
      <SEO {...pageSEO.home} />
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-purple-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold"
      >
        Skip to main content
      </a>
      <PageBackground />
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#020414]/70 px-4 py-3 backdrop-blur-2xl md:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <a href="/" className="flex items-baseline gap-0.5 select-none cursor-pointer">
            <span className="text-sm font-extrabold text-white transition-colors mb-0.5">ai</span>
            <span className="text-xl font-bold text-white tracking-tight">
              Stream<span className="text-purple-400 font-light">Buddy</span>
            </span>
            <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">.com</span>
          </a>
        </div>

        {!user && (
          <nav className="hidden items-center gap-7 text-[13px] font-semibold text-white/60 lg:flex" aria-label="Homepage">
            <a href="#features" className="transition hover:text-white">Features</a>
            <a href="#how-it-works" className="transition hover:text-white">How it works</a>
            <a href="#guides" className="transition hover:text-white">Guides</a>
            <a href="/faq" className="transition hover:text-white">FAQ</a>
          </nav>
        )}

        <div className="flex items-center gap-3">
          {user && (
            <a
              href="/guides"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              Guides
            </a>
          )}
          {user && user.subscriptionStatus !== 'active' && (
            <a
              href="/demo"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              Try StreamBuddy
            </a>
          )}
          {!user && (
            <>
              <button
                type="button"
                onClick={handleLogin}
                className="hidden rounded-full px-4 py-2 text-sm font-semibold text-white/70 transition hover:text-white sm:inline-flex"
              >
                Log in
              </button>
              <a
                href="/demo"
                className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-[linear-gradient(135deg,rgba(236,72,153,0.92),rgba(99,102,241,0.92))] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_34px_rgba(99,102,241,0.32)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_42px_rgba(236,72,153,0.28)]"
              >
                Try StreamBuddy
              </a>
            </>
          )}
          {user && (
            <>
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                width={40}
                height={40}
                className="w-10 h-10 rounded-full border-2 border-purple-500 shadow-lg shadow-purple-500/20"
              />
              <div className="hidden sm:block">
                <p className="text-sm font-bold text-white leading-tight">{user.displayName}</p>
                <p className="text-[10px] text-purple-300 uppercase tracking-widest">{getSubscriptionBadgeLabel(user.subscriptionStatus)}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="ml-2 text-gray-400 hover:text-white transition-colors text-sm font-medium bg-white/5 hover:bg-white/10 py-2 px-4 rounded-lg"
              >
                Logout
              </button>
            </>
          )}
        </div>
        </div>
      </header>

      <main id="main-content" className="relative z-10 flex-1">
        {loading ? (
          <LoginSection onLogin={handleLogin} />
        ) : !user ? (
          <LoginSection onLogin={handleLogin} />
        ) : (
          <div className="container mx-auto px-6 py-12">
            <WebDashboard
              user={user}
              serverUrl={serverUrl}
            />
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function LoginSection({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 md:pt-16">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[740px] w-screen -translate-x-1/2 bg-[radial-gradient(circle_at_20%_18%,rgba(168,85,247,0.16),transparent_34%),radial-gradient(circle_at_78%_26%,rgba(34,211,238,0.14),transparent_32%)]" />

      <section className="relative grid min-h-[680px] items-center gap-10 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="max-w-2xl text-left">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-fuchsia-300/20 bg-white/[0.06] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.24em] text-fuchsia-100 shadow-[0_0_32px_rgba(217,70,239,0.18)]">
            <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-300 shadow-[0_0_12px_rgba(240,171,252,0.9)]" />
            AI voice co-host for streamers
          </div>

          <h1 className="max-w-[760px] text-5xl font-black leading-[0.95] tracking-tight text-white md:text-7xl">
            Your Voice AI Co-Host for <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 via-fuchsia-400 to-rose-400">Streaming.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-7 text-slate-300 md:text-lg">
            StreamBuddy listens to your stream, understands chat and gameplay, remembers viewer context, and speaks in real time so you can focus on entertaining.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onLogin}
              className="group relative inline-flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-[linear-gradient(135deg,rgba(236,72,153,0.95),rgba(99,102,241,0.95))] px-7 py-4 text-sm font-black text-white shadow-[0_18px_54px_rgba(168,85,247,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_22px_64px_rgba(236,72,153,0.32)] active:translate-y-0"
            >
              <span className="absolute inset-0 rounded-xl bg-white/10 opacity-0 transition-opacity group-hover:opacity-100" />
              <svg className="w-6 h-6 transition-transform group-hover:rotate-12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
              </svg>
              <span className="relative">Get Started</span>
            </button>

            <a
              href="/demo"
              className="group inline-flex items-center justify-center gap-3 rounded-xl border border-cyan-300/[0.14] bg-white/[0.045] px-7 py-4 text-sm font-black text-white shadow-[0_16px_42px_rgba(7,12,28,0.35)] transition hover:-translate-y-0.5 hover:border-cyan-200/30 hover:bg-cyan-300/[0.08] hover:shadow-[0_18px_48px_rgba(34,211,238,0.14)]"
              data-testid="landing-try-demo"
            >
              <span aria-hidden="true" className="relative grid h-8 w-8 place-items-center rounded-full border border-cyan-200/30 bg-[linear-gradient(135deg,rgba(34,211,238,0.18),rgba(168,85,247,0.28))] text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.18)] transition group-hover:scale-105 group-hover:border-cyan-100/50">
                <span className="absolute inset-1 rounded-full border border-white/10" />
                <span className="relative flex h-4 items-end gap-0.5">
                  {[7, 12, 9, 15, 10].map((height, index) => (
                    <span
                      key={index}
                      className="w-0.5 rounded-full bg-gradient-to-t from-cyan-300 to-fuchsia-300"
                      style={{ height }}
                    />
                  ))}
                </span>
              </span>
              Hear Demo
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-[11px] font-semibold text-slate-400">
            <span>AI voice responses</span>
            <span>Real-time context</span>
            <span>Works with OBS</span>
          </div>
        </div>

        <HeroConsole />
      </section>

      <section id="features" className="relative grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          ['Chat & Alerts', 'Reacts to Chat & Alerts', 'Engages with your community, reads alerts, celebrates wins, and keeps the energy high.'],
          ['Vision', "Sees What's Happening", 'Understands what is going on screen so responses are timely, relevant, and on point.'],
          ['Memory', 'Remembers Your Community', 'Tracks context across streams and viewer context to create a seamless personal experience.'],
          ['Control', 'Built for Your Control', 'Works inside OBS or as a browser source. You stay in control with auto or push-to-talk.'],
        ].map(([tag, title, description]) => (
          <div key={title} className="rounded-2xl border border-white/10 bg-[#070b21]/70 p-5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl transition hover:-translate-y-1 hover:border-fuchsia-300/30 hover:bg-[#0a0f2d]/80">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-violet-300/20 bg-violet-400/15 text-lg font-black text-violet-100 shadow-[0_0_30px_rgba(124,58,237,0.2)]">{tag.slice(0, 1)}</div>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-fuchsia-200/80">{tag}</span>
            </div>
            <h2 className="text-xl font-black tracking-tight text-white">{title}</h2>
            <p className="mt-3 text-sm leading-6 text-slate-400">{description}</p>
            <a href="/guides/twitch-ai-chatbot-for-streamers" className="mt-4 inline-flex text-sm font-bold text-fuchsia-300 transition hover:text-white">Learn more &gt;</a>
          </div>
        ))}
      </section>

      <section className="relative mt-5 rounded-2xl border border-white/10 bg-[#071027]/70 p-5 text-left backdrop-blur-xl md:p-8">
        <SectionKicker>On-stream use</SectionKicker>
        <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">What StreamBuddy adds to your stream</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <ValueCard title="Cuts the dead air" body="It helps carry the quiet patches so the stream keeps moving between moments." />
          <ValueCard title="Makes moments hit harder" body="Subs, donations, raids, chat spikes, and game swings all have more to work with." />
          <ValueCard title="Stays in your hands" body="Push-to-talk and the rest of the control layer keep StreamBuddy useful without letting it run wild." />
        </div>
      </section>

      <section id="guides" className="relative mt-5 rounded-2xl border border-white/10 bg-[#071027]/70 p-5 text-left backdrop-blur-xl md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <SectionKicker>Setup guides</SectionKicker>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">Start here</h2>
          </div>
          <a href="/guides" className="text-sm font-bold text-fuchsia-200 transition hover:text-white">Browse all guides &gt;</a>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <GuideCard step="1" href="/guides/how-to-add-twitch-alerts-to-obs" title="Add StreamBuddy to OBS" body="Copy the overlay URL, add one Browser Source, and put it where it belongs." />
          <GuideCard step="2" href="/guides/how-to-add-ai-voice-to-your-twitch-stream" title="Set the StreamBuddy voice" body="Pick the voice, tune the personality, and get the pacing where you want it." />
          <GuideCard step="3" href="/guides/twitch-tts-bot" title="Use StreamBuddy for Twitch TTS" body="Keep the voice simple if that is all you want, or open it up later without changing tools." />
        </div>
      </section>

      <a href="/demo" className="group relative mt-5 grid rounded-2xl border border-white/10 bg-[#071027]/70 p-5 text-left backdrop-blur-xl transition hover:border-fuchsia-300/30 hover:bg-[#0a1231]/80 md:grid-cols-[0.86fr_1.14fr] md:p-8">
        <div className="flex flex-col justify-center">
          <SectionKicker compact>Live voice demo</SectionKicker>
          <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">Hear how StreamBuddy sounds <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-violet-300 to-fuchsia-400">before you set it up.</span></h2>
          <p className="mt-4 max-w-md text-sm leading-6 text-slate-400">Pick a voice, hold space to talk, and try a live stream moment in the browser.</p>
          <span className="mt-7 text-sm font-bold text-fuchsia-200 transition group-hover:text-white">Try the live demo &gt;</span>
        </div>
        <DemoBooth />
      </a>

      <section id="how-it-works" className="relative mt-8">
        <SectionKicker>How it works</SectionKicker>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          <StepItem step="1" title="Connect" body="Link your stream and add StreamBuddy to OBS in just a few clicks." />
          <StepItem step="2" title="Customize" body="Set voice, tone, interests, and rules so it sounds like you." />
          <StepItem step="3" title="Go Live" body="Start your stream. StreamBuddy listens, understands, and prepares." />
          <StepItem step="4" title="Let It Co-Host" body="It speaks in real time, reacts to moments, and keeps the vibe going." />
        </div>
      </section>

      <section className="relative mt-5 grid gap-5 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="rounded-2xl border border-white/10 bg-[#071027]/70 p-5 text-left backdrop-blur-xl md:p-8">
          <SectionKicker>FAQ</SectionKicker>
          <div className="mt-5 divide-y divide-white/10 rounded-xl border border-white/10 bg-black/20">
            {[
              ['Can viewers interact with the AI?', 'Yes. Viewers can trigger moments through chat, channel points, and stream events like subs, donations, raids, and follows.'],
              ['Can I use push-to-talk?', 'Yes. Push-to-talk is part of the control model, alongside hands-free mode.'],
              ['What voices are available?', 'You can choose and tune voices inside the app so the pacing fits your stream.'],
              ['What about privacy?', 'Your overlay URL should stay private, and the app is designed around creator-controlled setup.'],
            ].map(([question, answer]) => (
              <details key={question} className="group px-4 py-3">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-white">
                  {question}
                  <span className="text-lg text-fuchsia-300 transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-6 text-slate-400">{answer}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#071027]/70 p-6 text-center backdrop-blur-xl md:p-8">
          <div className="mx-auto max-w-xl">
            <h2 className="text-3xl font-black tracking-tight text-white md:text-4xl">Ready to stream with a real AI co-host?</h2>
            <p className="mt-4 text-sm leading-6 text-slate-400">StreamBuddy talks so you can focus on the game, the jokes, and your community.</p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <button type="button" onClick={onLogin} className="rounded-xl bg-[linear-gradient(135deg,rgba(236,72,153,0.95),rgba(99,102,241,0.95))] px-6 py-3 text-sm font-black text-white transition hover:-translate-y-0.5">
                Try StreamBuddy
              </button>
              <a href="/demo" className="rounded-xl border border-white/10 bg-white/[0.04] px-6 py-3 text-sm font-black text-white transition hover:bg-white/[0.08]">Hear Demo</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionKicker({ children, compact = false }: { children: React.ReactNode; compact?: boolean }) {
  return (
    <div className={`inline-flex w-fit rounded-full border border-fuchsia-300/20 bg-fuchsia-300/10 px-3 py-1 text-[10px] font-black uppercase text-fuchsia-200 ${compact ? 'tracking-[0.18em]' : 'tracking-[0.24em]'}`}>
      {children}
    </div>
  );
}

function HeroConsole() {
  return (
    <div className="relative min-h-[560px] sm:min-h-[500px]">
      <div className="absolute inset-0 rounded-[28px] border border-violet-400/30 bg-[#05081f]/70 shadow-[0_0_80px_rgba(124,58,237,0.22)] backdrop-blur-xl sm:rounded-[34px]" />
      <div className="absolute inset-3 overflow-hidden rounded-[22px] border border-white/10 bg-[radial-gradient(circle_at_52%_16%,rgba(236,72,153,0.32),transparent_26%),radial-gradient(circle_at_62%_42%,rgba(34,211,238,0.22),transparent_22%),linear-gradient(145deg,rgba(11,16,42,0.96),rgba(4,7,22,0.94))] sm:inset-4 sm:rounded-[26px]">
        <div className="absolute left-5 right-5 top-5 z-20 flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.18em] text-white/75">
          <span className="rounded bg-rose-500 px-2 py-1 text-white">Live</span>
          <span>01:32:47</span>
          <span className="ml-auto rounded-full border border-amber-200/20 bg-amber-300/10 px-2.5 py-1 text-[10px] text-amber-200 md:ml-2">
            2,847 watching
          </span>
        </div>
        <div className="absolute inset-x-12 top-20 h-48 rounded-full bg-[radial-gradient(circle,rgba(244,114,182,0.28),transparent_58%)] blur-xl" />
        <div className="absolute left-1/2 top-[42%] hidden h-28 w-28 -translate-x-1/2 place-items-center rounded-full border border-cyan-200/25 bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.24),transparent_18%),linear-gradient(135deg,rgba(168,85,247,0.26),rgba(34,211,238,0.2))] shadow-[0_0_48px_rgba(34,211,238,0.28),inset_0_0_36px_rgba(217,70,239,0.16)] sm:grid">
          <div className="absolute inset-3 rounded-full border border-white/10" />
          <div className="absolute -left-9 top-1/2 h-px w-9 bg-gradient-to-l from-cyan-300/70 to-transparent" />
          <div className="absolute -right-9 top-1/2 h-px w-9 bg-gradient-to-r from-fuchsia-300/70 to-transparent" />
          <svg className="relative h-11 w-11 text-white drop-shadow-[0_0_18px_rgba(255,255,255,0.34)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18a3 3 0 003-3V6a3 3 0 10-6 0v9a3 3 0 003 3z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11v4a7 7 0 01-14 0v-4M12 22v-4" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-36 bg-[linear-gradient(180deg,transparent,rgba(7,10,30,0.92))]" />
      </div>

      <div className="absolute left-6 right-6 top-[130px] z-20 rounded-2xl border border-white/10 bg-[#0b0d2d]/90 p-4 shadow-[0_22px_70px_rgba(0,0,0,0.38)] backdrop-blur-xl sm:left-0 sm:right-auto sm:top-[30%] sm:w-[260px] sm:p-5 md:-left-7 md:top-[28%] md:w-[280px]">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-400/20 text-xl font-black text-violet-100">ai</div>
          <div>
            <div className="text-sm font-black text-emerald-300">Speaking now</div>
            <div className="text-xs text-slate-400">Voice response active</div>
          </div>
        </div>
        <div className="mt-5 flex h-12 items-end gap-1 overflow-hidden">
          {[24, 38, 18, 46, 28, 56, 34, 22, 44, 30, 52, 26, 40, 20, 48, 31, 57, 36, 24].map((height, index) => (
            <span key={`hero-wave-${index}`} className="w-1.5 shrink-0 rounded-full bg-gradient-to-t from-cyan-400 to-fuchsia-400" style={{ height }} />
          ))}
        </div>
      </div>

      <div className="absolute -right-4 top-10 hidden w-[210px] rounded-2xl border border-white/10 bg-[#080d28]/85 p-4 text-xs text-slate-300 shadow-[0_22px_70px_rgba(0,0,0,0.32)] backdrop-blur-xl md:block">
        <div className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/50">Stream context</div>
        {[
          ['Chat messages', '342'],
          ['New follows', '12'],
          ['Subscriptions', '8'],
          ['Donations', '5'],
        ].map(([label, value]) => (
          <div key={label} className="flex justify-between border-t border-white/10 py-2">
            <span>{label}</span>
            <span className="font-bold text-white">{value}</span>
          </div>
        ))}
      </div>

      <div className="absolute bottom-6 left-6 right-6 z-30 grid gap-2 rounded-2xl border border-white/10 bg-black/30 p-3 backdrop-blur-xl sm:bottom-9 sm:left-9 sm:right-9 sm:grid-cols-4">
        {['Voice ON', 'Vision ON', 'Memory ON', 'Mode AUTO'].map((item) => (
          <div key={item} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 text-center text-[10px] font-black uppercase tracking-[0.16em] text-cyan-200">{item}</div>
        ))}
      </div>
    </div>
  );
}

function ValueCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <h3 className="text-lg font-black text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-400">{body}</p>
    </div>
  );
}

function GuideCard({ step, href, title, body }: { step: string; href: string; title: string; body: string }) {
  return (
    <a href={href} className="group rounded-2xl border border-white/10 bg-black/20 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:-translate-y-1 hover:border-fuchsia-300/30 hover:bg-black/30">
      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-violet-300/30 bg-violet-400/15 text-sm font-black text-violet-100">{step}</div>
      <h3 className="mt-4 text-lg font-black text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-400">{body}</p>
      <div className="mt-4 text-sm font-bold text-fuchsia-300 transition group-hover:text-white">Open guide &gt;</div>
    </a>
  );
}

function DemoBooth() {
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-black/32 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.34)] md:mt-0">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em] text-white/40">StreamBuddy Demo Booth</div>
          <div className="mt-1 text-xl font-black text-white">Voice preview</div>
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-white/60">Push to talk</span>
      </div>
      <div className="my-6 flex h-20 items-end justify-center gap-1.5">
        {[20, 34, 44, 58, 46, 66, 76, 54, 72, 86, 64, 48, 74, 58, 42, 30].map((height, index) => (
          <span key={`demo-wave-${index}`} className="w-2 rounded-full bg-gradient-to-t from-cyan-400 via-violet-400 to-fuchsia-400" style={{ height }} />
        ))}
      </div>
      <div className="space-y-3">
        <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/10 p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200">You</div>
          <p className="mt-1 text-sm text-white/80">How would StreamBuddy hype the chat after that clutch play?</p>
        </div>
        <div className="rounded-xl border border-fuchsia-300/20 bg-fuchsia-400/10 p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-fuchsia-200">StreamBuddy</div>
          <p className="mt-1 text-sm text-white/80">That was huge. The chat showed up and you sealed it. Let's keep this energy rolling.</p>
        </div>
      </div>
    </div>
  );
}

function StepItem({ step, title, body }: { step: string; title: string; body: string }) {
  return (
    <div className="text-left">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-violet-300/30 bg-violet-400/20 text-sm font-black text-white shadow-[0_0_24px_rgba(124,58,237,0.28)]">{step}</div>
      <h3 className="text-lg font-black text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-400">{body}</p>
    </div>
  );
}

interface WebDashboardProps {
  user: User;
  serverUrl: string;
}

function WebDashboard({ user, serverUrl }: WebDashboardProps) {
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState<string | null>(null);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const testOverride = localStorage.getItem('debugSubscription');
        if (testOverride === 'active' || testOverride === 'inactive' || testOverride === 'past_due' || testOverride === 'canceled') {
          setCurrentSubscriptionStatus(testOverride);
          return;
        }

        const res = await fetch('/api/billing/subscription', { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as { status?: string; subscriptionStatus?: string };
          const status = data.status || data.subscriptionStatus;
          if (status) {
            setCurrentSubscriptionStatus(status);
          }
        }
      } catch (e) {
        console.error('Failed to fetch subscription status:', e);
      }
    };
    void fetchSubscription();
    const interval = setInterval(() => { void fetchSubscription(); }, 10000);
    return () => clearInterval(interval);
  }, []);

  const isSubscribed = (currentSubscriptionStatus || user.subscriptionStatus) === 'active';

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-4xl font-black text-white tracking-tight">Welcome, {user.displayName}!</h2>
        <p className="text-gray-400 mt-2">Let's get you set up.</p>
      </div>

      <div className="space-y-8">
        {isSubscribed ? (
          <section className="group relative overflow-hidden rounded-[30px] border border-emerald-400/25 bg-gradient-to-br from-emerald-500/12 via-cyan-500/10 to-blue-500/12 p-[1px]">
            <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="relative rounded-[29px] bg-[#07111b]/90 px-6 py-6 backdrop-blur-xl md:px-8 md:py-8">
              <div className="grid gap-8 lg:grid-cols-[1.35fr_0.9fr] lg:items-start">
                <div className="max-w-2xl pt-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.24em] text-emerald-200">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    Pro Active
                  </div>
                  <h3 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">Your plan is active.</h3>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 md:text-[15px]">
                    Download the app, get it live in OBS, and keep your setup sharp.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href="https://github.com/aistreambuddy/streambuddy-releases/releases/latest/download/StreamBuddy-Setup-latest.exe"
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-black transition hover:bg-gray-100"
                    >
                      Download app
                    </a>
                    <a
                      href="/guides"
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/6 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                    >
                      Open guides
                    </a>
                  </div>

                  <div className="mt-8 grid gap-4 border-t border-white/10 pt-6 md:grid-cols-3">
                    <div className="border-l border-white/10 pl-4 first:border-l-0 first:pl-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">1. Install</div>
                      <p className="mt-2 text-sm leading-6 text-slate-300">Grab the Windows app and sign in with this account.</p>
                    </div>
                    <div className="border-l border-white/10 pl-4 first:border-l-0 first:pl-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">2. Add to OBS</div>
                      <p className="mt-2 text-sm leading-6 text-slate-300">Open Audio Bridge in the app and drop the source into your scene.</p>
                    </div>
                    <div className="border-l border-white/10 pl-4 first:border-l-0 first:pl-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">3. Set the voice</div>
                      <p className="mt-2 text-sm leading-6 text-slate-300">Dial in the voice, personality, pacing, and control mode.</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-4 rounded-[24px] border border-white/10 bg-black/20 p-5">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Account</div>
                    <div className="mt-3 text-2xl font-black tracking-tight text-white">$25 / month</div>
                    <p className="mt-2 text-sm leading-6 text-slate-300">Pro is active on this account.</p>
                  </div>

                  <div className="space-y-3 border-t border-white/10 pt-4 text-sm">
                    <a
                      href={`${serverUrl}/api/billing/portal`}
                      className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-semibold text-white transition hover:bg-white/10"
                    >
                      <span>Manage subscription</span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                    <a
                      href="/faq"
                      className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-semibold text-white transition hover:bg-white/10"
                    >
                      <span>Open FAQ</span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                    <a
                      href="https://discord.gg/Mu4fhhnaQK"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-semibold text-white transition hover:bg-white/10"
                    >
                      <span>Join Discord</span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Account</div>
                <h3 className="mt-3 text-lg font-bold text-white">Upgrade required</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Unlock the desktop app and the full live setup when you are ready.
                </p>
                <a
                  href={`${serverUrl}/api/billing/portal`}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-cyan-200"
                >
                  Unlock Pro
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Desktop App</div>
                <h3 className="mt-3 text-lg font-bold text-white">Install or update it</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Use the app for Audio Bridge, voice settings, hotkeys, and everything you run live on stream.
                </p>
                <a
                  href="https://github.com/aistreambuddy/streambuddy-releases/releases/latest/download/StreamBuddy-Setup-latest.exe"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-cyan-200"
                >
                  Download for Windows 10/11
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M12 5v14m0 0l6-6m-6 6l-6-6" />
                  </svg>
                </a>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Help</div>
                <div className="mt-4 flex flex-col gap-3">
                  <a href="/guides" className="text-sm font-semibold text-white transition hover:text-cyan-200">Open guides</a>
                  <a href="/faq" className="text-sm font-semibold text-white transition hover:text-cyan-200">Open FAQ</a>
                  <a href="https://discord.gg/Mu4fhhnaQK" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-white transition hover:text-cyan-200">Join Discord</a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Pro Plan CTA */}
        {!isSubscribed && (
          <div className="group relative bg-gradient-to-b from-[#1a103c] to-[#0a0a0f] border border-purple-500/30 rounded-3xl p-[1px] overflow-hidden transition-transform hover:scale-[1.01] duration-500 will-change-transform transform-gpu">
            {/* Animated Gradient Border */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 opacity-20 group-hover:opacity-40 transition-opacity duration-1000 animate-gradient-xy rounded-[inherit]" />

            <div className="relative bg-[#0a0a0f]/90 backdrop-blur-xl rounded-[23px] p-8 md:p-10 h-full">
              {/* Background FX */}
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-600/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />

              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12">
                {/* Left Column: Value Prop */}
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full px-4 py-1.5 mb-6">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                    </span>
                    <span className="text-purple-200 text-xs font-bold uppercase tracking-widest">Early Access Pricing</span>
                  </div>

                  <h3 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight leading-tight">
                    Go Pro. <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Stream Smarter.</span>
                  </h3>

                  <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-md mx-auto md:mx-0">
                    Unlock the full power of AI. Engage your chat, monetize interactions, and grow your community with the ultimate co-streaming companion.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
                    <a
                      href={`${serverUrl}/api/billing/portal`}
                      className="group/btn relative w-full sm:w-auto bg-white text-black hover:bg-gray-100 px-8 py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] flex items-center justify-center gap-2"
                    >
                      <span>Unlock Pro Access</span>
                      <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                    <div className="text-left pl-2">
                      <div className="text-white font-bold text-lg leading-none">$25.00<span className="text-sm text-gray-500 font-normal">/mo</span></div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">Cancel anytime</div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Feature Grid */}
                <div className="w-full md:w-[480px] grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Feature Cards */}
                  <div className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-purple-500/30 p-4 rounded-2xl transition-all duration-300 group/card">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-xl mb-3 group-hover/card:scale-110 transition-transform">🎙️</div>
                    <div className="font-bold text-white text-sm mb-1">AI Co-Host</div>
                    <div className="text-xs text-gray-400 leading-relaxed">Natural voice that learns about you and your community. Remembers details and engages with perfect timing.</div>
                  </div>

                  <div className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-green-500/30 p-4 rounded-2xl transition-all duration-300 group/card">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center text-xl mb-3 group-hover/card:scale-110 transition-transform">🎮</div>
                    <div className="font-bold text-white text-sm mb-1">Vision AI</div>
                    <div className="text-xs text-gray-400 leading-relaxed">AI watches your gameplay and comments on epic moments—clutch plays, deaths, and rare loot.</div>
                  </div>

                  <div className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-pink-500/30 p-4 rounded-2xl transition-all duration-300 group/card">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center text-xl mb-3 group-hover/card:scale-110 transition-transform">🎭</div>
                    <div className="font-bold text-white text-sm mb-1">Custom Personality</div>
                    <div className="text-xs text-gray-400 leading-relaxed">Design your AI's vibe, from sarcastic roaster to hype teammate. Full personality control.</div>
                  </div>

                  <div className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-blue-500/30 p-4 rounded-2xl transition-all duration-300 group/card">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center text-xl mb-3 group-hover/card:scale-110 transition-transform">🔊</div>
                    <div className="font-bold text-white text-sm mb-1">Push-to-Talk</div>
                    <div className="text-xs text-gray-400 leading-relaxed">Control when your AI speaks with global hotkeys. No unwanted interruptions.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Download App CTA */}
        <div id="download" className="group relative bg-gradient-to-b from-[#1a103c] to-[#0a0a0f] border border-purple-500/30 rounded-3xl p-[1px] overflow-hidden transition-transform hover:scale-[1.01] duration-500 will-change-transform transform-gpu">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 opacity-20 group-hover:opacity-40 transition-opacity duration-1000 animate-gradient-xy rounded-[inherit]" />

          <div className="relative bg-[#0a0a0f]/90 backdrop-blur-xl rounded-[23px] p-8 overflow-hidden h-full">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 flex items-center justify-center shadow-lg shadow-purple-500/10">
                  <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white mb-2 tracking-tight">Desktop App</h3>
                  <p className="text-gray-400 text-sm max-w-sm leading-relaxed">
                    Required for global hotkeys and high-fidelity audio capture. The engine of your AI companion.
                  </p>
                </div>
              </div>

              <a
                href="https://github.com/aistreambuddy/streambuddy-releases/releases/latest/download/StreamBuddy-Setup-latest.exe"
                className="w-full md:w-auto bg-white text-black hover:bg-gray-100 font-black py-4 px-8 rounded-xl text-sm transition-all flex items-center justify-center gap-3 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:scale-105"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M0 3.449L9.75 2.1v9.451l-9.75 1.4V3.45zM10.5 2l13.5-1.9v10.451l-13.5 1.4V2zm0 11.549l13.5 1.4V25l-13.5-1.9V13.549zM0 13.449l9.75 1.4V22.9l-9.75-1.4V13.45z" />
                </svg>
                <div className="text-left">
                  <div className="leading-none text-xs text-gray-500 uppercase font-bold mb-0.5">Download for</div>
                  <div className="leading-none text-base">Windows 10/11</div>
                </div>
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
